import max from 'lodash/max';
import { Edge, Node, XYPosition } from 'reactflow';
import { newUiId } from '@tymely/utils';

import { ArgNodeData, DecisionNodeData, EdgeData, PolicyGraph } from '../types/graph';

export const createArgNode = (position: XYPosition = { x: 0, y: 0 }): Node<ArgNodeData> => {
    const id = newUiId();
    return {
        id,
        type: 'arg',
        data: { kind: 'arg' },
        connectable: false,
        dragHandle: '.drag-handle',
        position,
    };
};

const generateDecisionNodeData = (name?: string): DecisionNodeData => ({
    kind: 'decision',
    workflow: {
        id: Math.floor(Math.random() * 0x7fffffff),
        status: 'TANDEM',
        actions: [],
        title: name || 'New Decision',
        auto: true,
        allow_resubmit: true,
        validation_enabled: true,
        requires_approval: false,
    },
});

export const createDecisionNode = ({
    id,
    name,
    position = { x: 0, y: 0 },
}: {
    id?: string;
    name?: string;
    position?: XYPosition;
}) => {
    return {
        id: id || newUiId(),
        type: 'decision',
        data: generateDecisionNodeData(name),
        connectable: false,
        dragHandle: '.drag-handle',
        position,
    };
};

export const adjustNodePositions = (existingNodes: Node[], newNodes: Node[], padding = 300) => {
    const calculateOffset = (existingCoords: number[], newCoords: number[], isY: boolean) => {
        if (existingCoords.length === 0 || newCoords.length === 0) {
            return 0;
        }
        const maxExisting = Math.max(...existingCoords);
        const minNew = Math.min(...newCoords);
        return isY ? maxExisting - minNew + padding : maxExisting - minNew;
    };

    const existingXCoords = existingNodes.map((node) => node.position?.x).filter((x) => x !== undefined);
    const existingYCoords = existingNodes.map((node) => node.position?.y).filter((y) => y !== undefined);
    const newXCoords = newNodes.map((node) => node.position?.x).filter((x) => x !== undefined);
    const newYCoords = newNodes.map((node) => node.position?.y).filter((y) => y !== undefined);

    const xOffset = calculateOffset(existingXCoords, newXCoords, false);
    const yOffset = calculateOffset(existingYCoords, newYCoords, true);
    return newNodes.map((node) => ({
        ...node,
        position: {
            x: node.position.x + xOffset,
            y: node.position.y + yOffset,
        },
    }));
};

export const addArgNodeInGraph = (argumentMetadataId: number, position: XYPosition, graph: PolicyGraph) => {
    const baseNode = createArgNode(position);
    const node: Node<ArgNodeData> = {
        ...baseNode,
        data: {
            ...baseNode.data,
            arg_metadata_id: argumentMetadataId,
        },
    };

    const [adjustedNode] = adjustNodePositions(graph.nodes as Node[], [node]);

    return {
        updatedGraph: { ...graph, nodes: [...graph.nodes, adjustedNode] },
        newNode: adjustedNode,
    };
};

export const addDecisionNodeInGraph = (name: string, position: XYPosition, graph: PolicyGraph) => {
    const node = createDecisionNode({ name, position });
    const adjustedNode = adjustNodePositions(graph.nodes as Node[], [node]);
    return {
        updatedGraph: { ...graph, nodes: [...graph.nodes, adjustedNode[0]] },
        newNode: adjustedNode[0],
    };
};

type CreateConditionEdge = {
    source: string;
    target: string;
    edges: Edge<EdgeData>[];
    oldEdgeData?: Partial<EdgeData>;
};

export const createConditionEdge = ({ source, target, edges, oldEdgeData }: CreateConditionEdge): Edge<EdgeData> => {
    const currentMaxOrder =
        max(edges.filter((edge) => edge.source === source && edge.data).map((edge) => edge.data?.order)) ?? 0;

    const id = newUiId();
    return {
        id,
        source,
        target,
        data: { order: currentMaxOrder + 1, ...oldEdgeData },
    };
};
